import { GRID_SECTION_HANDLE, SECTION_FETCH_SKIP_PARAMS } from "./constants";
import { fetchSectionHtml, getGridSection } from "./dom";
import { buildSectionUrl, mergeSearchParams } from "./urls";

const loadMoreObservers = new WeakMap<Element, IntersectionObserver>();

declare global {
  interface Window {
    __ekLoadMoreInFlight?: boolean;
    ekRefreshCollectionLoadMore?: () => void;
    ProductCardSwatches?: {
      initCard: (card: Element, options?: object) => void;
      initAll: (root?: ParentNode, options?: object) => void;
    };
    BannerSlider?: {
      init: (container?: ParentNode) => void;
    };
  }
}

const parseNextPage = (value: string | undefined) => {
  const parsed = Number.parseInt(value ?? "", 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

// ponytail: trigger ~2 grid rows before the end; upgrade path is a configurable data attribute.
const LOAD_MORE_LEAD_CARDS = 8;
const LOAD_MORE_ROOT_MARGIN = "0px 0px 1200px 0px";

const getLoadedPages = (gridSection: HTMLElement) => {
  const raw = gridSection.dataset.ekLoadedPages;
  if (!raw) return new Set([1]);
  return new Set(
    raw
      .split(",")
      .map((value) => parseNextPage(value))
      .filter((page) => page > 0),
  );
};

const markPageLoaded = (gridSection: HTMLElement, page: number) => {
  const pages = getLoadedPages(gridSection);
  pages.add(page);
  gridSection.dataset.ekLoadedPages = Array.from(pages)
    .sort((a, b) => a - b)
    .join(",");
};

const unmarkPageLoaded = (gridSection: HTMLElement, page: number) => {
  const pages = getLoadedPages(gridSection);
  pages.delete(page);
  if (pages.size === 0) {
    delete gridSection.dataset.ekLoadedPages;
    return;
  }
  gridSection.dataset.ekLoadedPages = Array.from(pages)
    .sort((a, b) => a - b)
    .join(",");
};

export const resetLoadMorePageState = (gridSection: HTMLElement) => {
  delete gridSection.dataset.ekLoadedPages;
};

const getLoadMoreObserveTarget = (gridSection: HTMLElement, sentinel: HTMLElement) => {
  const cards = gridSection.querySelectorAll("#products-grid .product-card");
  if (cards.length > 0) {
    const leadIndex = Math.max(0, cards.length - LOAD_MORE_LEAD_CARDS);
    return cards[leadIndex] as HTMLElement;
  }
  return sentinel;
};

const appendGridPage = (existingGrid: HTMLElement, newGrid: HTMLElement) => {
  const existingHandles = new Set(
    Array.from(existingGrid.querySelectorAll<HTMLElement>(".product-card[data-product-handle]"))
      .map((card) => card.dataset.productHandle)
      .filter((handle): handle is string => Boolean(handle)),
  );

  const toAppend: Element[] = [];
  for (const node of Array.from(newGrid.children)) {
    const el = node as HTMLElement;
    const handle = el.classList.contains("product-card") ? el.dataset.productHandle : null;
    if (handle && existingHandles.has(handle)) continue;
    if (handle) existingHandles.add(handle);
    toAppend.push(node);
  }

  for (const node of toAppend) {
    existingGrid.appendChild(node);
  }

  return toAppend;
};

export const initLoadMore = (gridSection: HTMLElement) => {
  const sentinel = gridSection.querySelector<HTMLElement>("#load-more-sentinel");
  if (!sentinel) return;

  loadMoreObservers.get(gridSection)?.disconnect();

  sentinel.innerHTML = `<div class="flex justify-center py-8" role="status" aria-hidden="true"><div class="w-8 h-8 rounded-full border-4 border-current border-t-transparent animate-spin text-gray-500 opacity-0"></div></div>`;
  const spinner = sentinel.querySelector<HTMLElement>("[role='status']");
  const spinnerIcon = sentinel.querySelector<HTMLElement>(".animate-spin");
  let loading = false;
  let observeTarget = getLoadMoreObserveTarget(gridSection, sentinel);

  const bindObserveTarget = () => {
    if (!sentinel.isConnected || !sentinel.dataset.nextPage) return;
    observer.unobserve(observeTarget);
    observeTarget = getLoadMoreObserveTarget(gridSection, sentinel);
    observer.observe(observeTarget);
  };

  const scheduleReobserve = () => {
    requestAnimationFrame(() => {
      if (!loading) bindObserveTarget();
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries[0]?.isIntersecting || loading) return;

      const nextPage = parseNextPage(sentinel.dataset.nextPage);
      if (!nextPage) return;
      if (getLoadedPages(gridSection).has(nextPage)) {
        scheduleReobserve();
        return;
      }

      loading = true;
      window.__ekLoadMoreInFlight = true;
      markPageLoaded(gridSection, nextPage);
      observer.unobserve(observeTarget);
      spinner?.setAttribute("aria-hidden", "false");
      spinnerIcon?.classList.remove("opacity-0");

      const ref = new URL(window.location.href);
      const baseUrl = new URL(ref.pathname, ref.origin);
      mergeSearchParams(baseUrl, ref, SECTION_FETCH_SKIP_PARAMS);
      baseUrl.searchParams.set("page", String(nextPage));
      const navParent = ref.searchParams.get("nav_parent");
      const fetchUrl = buildSectionUrl(baseUrl.pathname + baseUrl.search, GRID_SECTION_HANDLE, navParent);

      fetch(fetchUrl, { headers: { "X-Requested-With": "XMLHttpRequest" } })
        .then((resp) => (resp.ok ? resp.text() : Promise.reject()))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const remoteRoot = doc.querySelector(".js-collection-section") || doc.body;
          const newGrid = remoteRoot.querySelector("#products-grid");
          const existingGrid = gridSection.querySelector("#products-grid");
          if (newGrid && existingGrid) {
            const appended = appendGridPage(existingGrid, newGrid);
            for (const card of appended.filter((node) => (node as HTMLElement).classList?.contains("product-card"))) {
              window.ProductCardSwatches?.initCard?.(card);
            }
            window.BannerSlider?.init(existingGrid);
          }

          const newSentinel = remoteRoot.querySelector<HTMLElement>("#load-more-sentinel");
          if (newSentinel) {
            sentinel.dataset.nextPage = newSentinel.dataset.nextPage || "";
          } else {
            observer.disconnect();
            loadMoreObservers.delete(gridSection);
            sentinel.remove();
          }
        })
        .catch(() => {
          unmarkPageLoaded(gridSection, nextPage);
          observer.disconnect();
          loadMoreObservers.delete(gridSection);
          sentinel.remove();
        })
        .finally(() => {
          window.__ekLoadMoreInFlight = false;
          loading = false;
          if (sentinel.isConnected) {
            spinner?.setAttribute("aria-hidden", "true");
            spinnerIcon?.classList.add("opacity-0");
            scheduleReobserve();
          }
        });
    },
    { rootMargin: LOAD_MORE_ROOT_MARGIN },
  );

  loadMoreObservers.set(gridSection, observer);
  observer.observe(observeTarget);
};

export const refreshCollectionLoadMore = () => {
  const gridSection = getGridSection();
  if (!gridSection) return;
  initLoadMore(gridSection);
};

window.ekRefreshCollectionLoadMore = refreshCollectionLoadMore;
